import { useEffect, useState } from 'react';
import { findFocusedRoute } from '@react-navigation/native';
import { navigationRef } from '../navigation/rootNavigation';

/**
 * CORE-311 — focused route name for the WEB Agapella launcher, read from the
 * navigation container's own state rather than from the navigator.
 *
 * {@link ./useCurrentRouteName} asks `getCurrentRoute()`, which goes to the root
 * navigator — and the navigator sits inside the lazy-screen Suspense (CORE-638).
 * Navigating to a screen whose chunk hasn't arrived suspends it, and until the
 * chunk lands it reports the PREVIOUS route; when the chunk does land the
 * container state doesn't change again, so no further `state` event comes. On
 * the first visit to «Сообщения» in a session the launcher therefore stayed on
 * top of the chat list. The container state is written synchronously by the
 * dispatch, so it names the new screen while its chunk is still downloading.
 *
 * Web widget only: the shared hook also feeds native surfaces, out of scope here.
 */
export function useFocusedRouteName(): string | undefined {
  const [name, setName] = useState<string | undefined>(readFocusedRouteName);

  useEffect(() => {
    const read = () => setName(readFocusedRouteName());
    read();
    return navigationRef.addListener('state', read);
  }, []);

  return name;
}

function readFocusedRouteName(): string | undefined {
  // `current` is attached as soon as the container mounts — before the
  // suspended navigator inside it is ready, which is the window that matters.
  if (navigationRef.current == null) return undefined;
  return findFocusedRoute(navigationRef.getState())?.name;
}
